/**
 * RunResults: a template run's rows as a table or JSON, with CSV and JSON export, the run's status line and the
 * pages that failed. The table starts with the template's columns; "Columns" adds any other field the run returned
 * (remembered per template in this browser), and the CSV follows what is shown.
 *
 *   <RunResults template={t} run={run} columns={cols} onColumnsChange={setCols} />
 *   <ColumnsPreview template={t} columns={cols} onColumnsChange={setCols} />   before a run: the columns you will get
 *
 * The page owns the column choice (useTemplateColumns), so a preset's columns show before it runs.
 */
import { useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { Icon } from "@iconify/react";
import Button from "@/components/ui/Button";
import SegmentedControl from "@/components/ui/SegmentedControl";
import { StatusText } from "@/components/ui/TableCells";
import { useTheme } from "@/context/ThemeContext";
import { EMPTY, formatDateTime, formatNumber, formatUrlForDisplay } from "@/lib/format";
import type { TemplateColumn, TemplateDetail, TemplateRun } from "@/services/templates.service";
import ColumnPicker from "./ColumnPicker";
import { discoverFields } from "./fields";

/** The picker's change handler, from the picked keys back to column definitions. */
function pickColumns(keys: string[], known: TemplateColumn[]): TemplateColumn[] {
  const byKey = new Map(known.map(c => [c.key, c]));
  return keys.map(k => byKey.get(k)).filter((c): c is TemplateColumn => !!c);
}

/**
 * The extra fields on offer, plus any offered earlier on this page: unticking a picked field that this run or sample
 * doesn't have keeps it in the list, so it can be ticked again.
 */
function useOffered(fields: TemplateColumn[], slug: string): TemplateColumn[] {
  const seen = useRef<{ slug: string; byKey: Map<string, TemplateColumn> }>({ slug, byKey: new Map() });
  if (seen.current.slug !== slug) seen.current = { slug, byKey: new Map() };
  const byKey = seen.current.byKey;
  for (const f of fields) byKey.set(f.key, f);
  const current = new Set(fields.map(f => f.key));
  return [...fields, ...[...byKey.values()].filter(f => !current.has(f.key))];
}

const CodeHighlight = dynamic(() => import("@/components/ui/CodeHighlight"), { ssr: false });

type Rec = Record<string, unknown>;

export const getPath = (obj: unknown, path: string): unknown =>
  path.split(".").reduce<unknown>((o, k) => (o == null || typeof o !== "object" ? undefined : (o as Rec)[k]), obj);

/** A site's code value ("SINGLE_FAMILY", "FOR_SALE") as words ("Single family"); anything else unchanged. */
function codeToWords(s: string): string {
  if (!/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(s)) return s;
  const words = s.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Plain text of a cell, for CSV and tooltips. */
export function cellText(v: unknown): string {
  if (v == null) return "";
  const named = (o: object) => {
    const r = o as Rec;
    const n = r.name ?? r.title ?? r.text ?? r.label;
    // A link keeps its address next to its name, so the table and the CSV don't lose it.
    const url = typeof r.url === "string" ? r.url : typeof r.href === "string" ? r.href : null;
    if (typeof n === "string") return url && url !== n ? `${n} (${url})` : n;
    return url ?? JSON.stringify(o);
  };
  if (Array.isArray(v)) return v.map(x => (x && typeof x === "object" ? named(x) : codeToWords(String(x)))).join(", ");
  if (typeof v === "object") return named(v);
  return typeof v === "string" ? codeToWords(v) : String(v);
}

function Cell({ column, value }: { column: TemplateColumn; value: unknown }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return <span className="text-fg-muted">{EMPTY}</span>;
  switch (column.format) {
    case "number":
      return <span className="tabular-nums">{typeof value === "number" ? formatNumber(value) : cellText(value)}</span>;
    case "boolean":
      return <span>{value ? "Yes" : "No"}</span>;
    case "url": {
      const href = String(value);
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block max-w-[16rem] truncate text-primary-text hover:underline" title={href}>
          {formatUrlForDisplay(href, 48)}
        </a>
      );
    }
    case "image":
      // eslint-disable-next-line @next/next/no-img-element
      return <img src={String(value)} alt="" loading="lazy" className="h-10 w-10 rounded-md border border-line object-cover" />;
    case "date": {
      const s = String(value);
      const d = /^\d{4}-\d{2}-\d{2}/.test(s) ? formatDateTime(s, "date") : s;
      return <span className="whitespace-nowrap">{d}</span>;
    }
    case "longtext": {
      const s = cellText(value);
      return (
        <span className="line-clamp-2 max-w-[28rem] text-fg-secondary" title={s.length > 600 ? s.slice(0, 600) + "…" : s}>
          {s}
        </span>
      );
    }
    default: {
      // A number in a text column is still a number: round it like the number columns do.
      const s = typeof value === "number" ? formatNumber(value) : cellText(value);
      return (
        <span className="line-clamp-2 max-w-[22rem]" title={s}>
          {s}
        </span>
      );
    }
  }
}

function toCsv(columns: TemplateColumn[], rows: Record<string, unknown>[]): string {
  const esc = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const head = columns.map(c => esc(c.label)).join(",");
  const body = rows.map(r => columns.map(c => esc(cellText(getPath(r, c.key)))).join(","));
  return [head, ...body].join("\r\n");
}

function download(name: string, type: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ColumnsPreview({
  template,
  columns,
  onColumnsChange,
}: {
  template: TemplateDetail;
  columns: TemplateColumn[];
  onColumnsChange: (next: TemplateColumn[]) => void;
}) {
  // Before a run, the fields on offer are the template's, any already picked, and those in its sample rows.
  const offered = useMemo(() => {
    const fromSample = discoverFields((template.sample ?? []) as Record<string, unknown>[], template.columns);
    const picked = columns.filter(c => !template.columns.some(t => t.key === c.key) && !fromSample.some(f => f.key === c.key));
    return [...picked, ...fromSample];
  }, [template, columns]);
  const extra = useOffered(offered, template.slug);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-13 text-fg-secondary">Each run returns a table with these columns. Fill in the form and press Run.</p>
        <ColumnPicker
          base={template.columns}
          extra={extra}
          visible={columns.map(c => c.key)}
          onChange={keys => onColumnsChange(pickColumns(keys, [...template.columns, ...extra]))}
        />
      </div>
      <ColumnsSkeleton columns={columns} />
    </div>
  );
}

function ColumnsSkeleton({ columns }: { columns: TemplateColumn[] }) {
  return (
    <div className="overflow-hidden rounded-card border border-dashed border-line-strong">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-left text-13">
          <thead className="bg-surface-muted text-xs font-medium text-fg-secondary">
            <tr>
              {columns.map(c => (
                <th key={c.key} scope="col" className="whitespace-nowrap px-3 py-2.5 font-medium">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map(r => (
              <tr key={r} className="border-t border-line">
                {columns.map(c => (
                  <td key={c.key} className="px-3 py-3">
                    <span className="block h-2.5 rounded-full bg-surface-muted" style={{ width: `${48 + ((r * 7 + c.key.length * 5) % 50)}px` }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RunResults({
  template,
  run,
  columns: chosen,
  onColumnsChange,
}: {
  template: TemplateDetail;
  run: TemplateRun;
  columns: TemplateColumn[];
  onColumnsChange: (next: TemplateColumn[]) => void;
}) {
  const [view, setView] = useState<"table" | "json">("table");
  const { resolvedTheme } = useTheme();
  const rows = useMemo(() => run.rows ?? [], [run.rows]);
  // Every other field this run returned. Picked fields this run doesn't have stay listed (empty cells) so a choice
  // made on another run isn't lost.
  const offered = useMemo(() => {
    const found = discoverFields(rows, template.columns);
    const kept = chosen.filter(c => !template.columns.some(t => t.key === c.key) && !found.some(f => f.key === c.key));
    return [...kept, ...found];
  }, [rows, template.columns, chosen]);
  const extraFields = useOffered(offered, template.slug);
  const columns = chosen.length ? chosen : template.columns;
  const chooseColumns = (keys: string[]) => onColumnsChange(pickColumns(keys, [...template.columns, ...extraFields]));
  const running = run.status === "running";
  const failures = run.failures ?? [];
  const fileBase = `${template.slug}-${(run.createdAt || "").slice(0, 10) || "run"}`;
  const json = useMemo(() => (view === "json" ? JSON.stringify(rows.map(({ _sourceUrl, ...r }) => ({ ...r, ...(_sourceUrl ? { sourceUrl: _sourceUrl } : {}) })), null, 2) : ""), [rows, view]);

  const statusLine = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-13 text-fg-secondary">
      <StatusText status={run.status === "partial" ? "partial" : run.status} />
      {run.progress && running && (
        <span className="tabular-nums">
          {run.progress.completed + run.progress.failed} of {run.progress.total} pages
        </span>
      )}
      {!running && template.hasRows && <span className="tabular-nums">{formatNumber(rows.length)} {rows.length === 1 ? "row" : "rows"}</span>}
      {!running && run.summary && (
        <span className="tabular-nums">
          {run.summary.averagePosition != null ? `Average position ${run.summary.averagePosition}, ranked in ${run.summary.ranked} of ${run.summary.groups}` : `Not ranked in any of ${run.summary.groups}`}
        </span>
      )}
      {!running && run.creditsUsed != null && (
        <span className="tabular-nums">
          {formatNumber(run.creditsUsed)} {run.creditsUsed === 1 ? "credit" : "credits"}
        </span>
      )}
    </div>
  );

  // Crawl and parse templates keep their results on the tool's own page.
  if (!template.hasRows) {
    return (
      <div className="space-y-4">
        {statusLine}
        {running ? (
          <p className="text-sm text-fg-secondary">This runs in the background. You can leave this page; it will be in your runs and in Logs.</p>
        ) : run.status === "failed" ? (
          <p className="text-sm text-danger-text">{run.error || "The run failed. Nothing was charged."}</p>
        ) : run.status === "partial" && run.error ? (
          <p className="text-sm text-warning-text">{run.error}</p>
        ) : null}
        {run.resultsPath && (
          <Button href={run.resultsPath} variant={running ? "secondary" : "primary"} leftIcon="lucide:arrow-up-right">
            {running ? "Watch progress" : "Open results"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {statusLine}
        {!running && rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {view === "table" && <ColumnPicker base={template.columns} extra={extraFields} visible={columns.map(c => c.key)} onChange={chooseColumns} />}
            <SegmentedControl
              aria-label="Results view"
              size="sm"
              value={view}
              onChange={setView}
              options={[
                { value: "table", label: "Table" },
                { value: "json", label: "JSON" },
              ]}
            />
            <Button size="sm" variant="secondary" leftIcon="lucide:download" onClick={() => download(`${fileBase}.csv`, "text/csv;charset=utf-8", "﻿" + toCsv(columns, rows))}>
              CSV
            </Button>
            <Button size="sm" variant="secondary" leftIcon="lucide:braces" onClick={() => download(`${fileBase}.json`, "application/json", JSON.stringify(rows, null, 2))}>
              JSON
            </Button>
          </div>
        )}
      </div>

      {run.status === "failed" && <p className="text-sm text-danger-text">{run.error || "The run failed. Nothing was charged."}</p>}
      {/* A partial run kept what it got; its error says which step stopped and why. */}
      {run.status === "partial" && run.error && <p className="text-sm text-warning-text">{run.error}</p>}

      {failures.length > 0 && !running && (
        <details className="rounded-control border border-warning/30 bg-warning-subtle px-3 py-2 text-13 text-warning-text">
          <summary className="cursor-pointer select-none font-medium">
            {failures.length} {failures.length === 1 ? "page" : "pages"} failed and {failures.length === 1 ? "was" : "were"} not charged
          </summary>
          <ul className="mt-2 space-y-1.5 text-fg-secondary">
            {failures.map(f => (
              <li key={f.url} className="break-words">
                <span className="font-medium text-fg">{formatUrlForDisplay(f.url, 70)}</span>: {f.error}
              </li>
            ))}
          </ul>
        </details>
      )}

      {running ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center" role="status">
          <Icon icon="lucide:loader-circle" className="text-2xl text-primary-text motion-safe:animate-spin" aria-hidden="true" />
          <p className="text-sm text-fg-secondary">Collecting results…</p>
          {run.progress && run.progress.total > 0 && (
            <div className="h-1.5 w-48 overflow-hidden rounded-full bg-surface-muted" aria-hidden="true">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${Math.max(6, ((run.progress.completed + run.progress.failed) / run.progress.total) * 100)}%` }}
              />
            </div>
          )}
        </div>
      ) : rows.length === 0 ? (
        run.status !== "failed" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center">
            <Icon icon="lucide:inbox" className="text-2xl text-fg-muted" aria-hidden="true" />
            <p className="text-sm text-fg-secondary">This run found nothing. Try other inputs.</p>
          </div>
        )
      ) : view === "json" ? (
        <div className="min-h-0 flex-1 overflow-auto rounded-control border border-line bg-surface-muted p-3">
          <CodeHighlight code={json.length > 400_000 ? json.slice(0, 400_000) + "\n…" : json} language="json" dark={resolvedTheme === "dark"} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-control border border-line">
          <table className="w-full min-w-max text-left text-13">
            <thead className="sticky top-0 z-10 bg-surface-muted text-xs text-fg-secondary shadow-[0_1px_0_var(--color-line)]">
              <tr>
                <th scope="col" className="px-3 py-2.5 text-right font-medium tabular-nums">
                  #
                </th>
                {columns.map(c => (
                  <th key={c.key} scope="col" className={`whitespace-nowrap px-3 py-2.5 font-medium ${c.format === "number" ? "text-right" : ""}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 2000).map((r, i) => (
                <tr key={i} className="border-t border-line align-top hover:bg-surface-muted/60">
                  <td className="px-3 py-2.5 text-right text-xs text-fg-muted tabular-nums">{i + 1}</td>
                  {columns.map(c => (
                    <td key={c.key} className={`px-3 py-2.5 text-fg ${c.format === "number" ? "text-right" : ""}`}>
                      <Cell column={c} value={getPath(r, c.key)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 2000 && <p className="border-t border-line px-3 py-2 text-xs text-fg-muted">Showing the first 2,000 rows. Download CSV or JSON for all {formatNumber(rows.length)}.</p>}
        </div>
      )}
    </div>
  );
}
